import { Info, Lightbulb, Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Slider,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import KnowledgeBaseSelect from "../../components/KnowledgeBaseSelect";
import LoadingButton from "../../components/LoadingButton";
import PopOutTextInput from "../../components/PopOutTextInput";
import { CAMPAIGN_FIELD_LIMITS } from "../../constants";
import { DEFAULT_CHANNELS_CONFIG } from "../../constants/contentTypes";
import {
  getContrastColor,
  getRandomVintageColor,
  hostname,
  parseError,
  vintageColors,
} from "../../utils";
import CampaignTypeSelect from "./CampaignTypeSelect";

const DEFAULT_CAMPAIGN = {
  name: "",
  plan: "",
  webResearch: false,
  knowledgeBase: false,
  knowledgeBaseScope: "/",
  tone: "professional",
  frequency: "manual",
  color: "", // Will be set randomly in useEffect
  channels: DEFAULT_CHANNELS_CONFIG,
  idealCustomerProfileId: "",
  contentStrategyId: "",
  brandVoiceId: "",
  brandImageId: "",
  // Content Calendar/Ideation fields
  isIdeationMode: false,
  numberOfPosts: 1,
  // Day of week preferences (0 = Monday, 1 = Tuesday, etc.)
  publishDaysOfWeek: [0, 1, 2, 3, 4], // Default to weekdays
};

export default ({
  open,
  onClose,
  companyId,
  company,
  getToken,
  eventListener,
  selectedCampaign,
  currentPlan,
}: {
  open: boolean;
  onClose: () => void;
  companyId: string;
  company: any;
  getToken: Function;
  eventListener: Function;
  selectedCampaign: any | null;
  currentPlan: string;
}) => {
  const [formData, setFormData] = useState<any>(DEFAULT_CAMPAIGN);
  const [loading, setLoading] = useState(false);
  const [fetchingICPs, setFetchingICPs] = useState(false);
  const [fetchingStrategies, setFetchingStrategies] = useState(false);
  const [fetchingVoices, setFetchingVoices] = useState(false);
  const [fetchingImages, setFetchingImages] = useState(false);
  const [idealCustomerProfiles, setIdealCustomerProfiles] = useState<any[]>([]);
  const [contentStrategies, setContentStrategies] = useState<any[]>([]);
  const [brandVoices, setBrandVoices] = useState<any[]>([]);
  const [brandImages, setBrandImages] = useState<any[]>([]);

  const hasContentTypeSelected = () => {
    if (!formData.channels) return false;
    return Object.values(formData.channels).some(
      (channel: any) => channel?.enabled === true
    );
  };

  const validForm = () => {
    if (!formData.name) {
      return false;
    }

    // Always require a campaign plan, regardless of mode
    if (!formData.plan) {
      return false;
    }

    // Require at least one content type to be selected
    if (!hasContentTypeSelected()) {
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validForm()) return;
    setLoading(true);
    const currentToken = await getToken();
    await axios
      .post(
        `${hostname}/content/campaigns`,
        { companyId, campaign: formData },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      )
      .then(() => {
        handleClose();
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Fetch ideal customer profiles
  const fetchIdealCustomerProfiles = async () => {
    if (!companyId) return;

    setFetchingICPs(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/content/profiles`, {
        params: { companyId, pageSize: 100 }, // Get all ICPs
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setIdealCustomerProfiles(response.data.items || []);
    } catch (error) {
      console.error("Error fetching ideal customer profiles:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setFetchingICPs(false);
    }
  };

  // Fetch content strategies
  const fetchContentStrategies = async () => {
    if (!companyId) return;

    setFetchingStrategies(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/content/strategies`, {
        params: { companyId, pageSize: 100 }, // Get all strategies
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setContentStrategies(response.data.items || []);
    } catch (error) {
      console.error("Error fetching content strategies:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setFetchingStrategies(false);
    }
  };

  // Fetch brand voices
  const fetchBrandVoices = async () => {
    if (!companyId) return;

    setFetchingVoices(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/content/brand-voices`, {
        params: { companyId, pageSize: 100 }, // Get all brand voices
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setBrandVoices(response.data.items || []);
    } catch (error) {
      console.error("Error fetching brand voices:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setFetchingVoices(false);
    }
  };

  // Fetch brand images
  const fetchBrandImages = async () => {
    if (!companyId) return;

    setFetchingImages(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/content/brand-images`, {
        params: { companyId, pageSize: 100 }, // Get all brand images
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setBrandImages(response.data.items || []);
    } catch (error) {
      console.error("Error fetching brand images:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setFetchingImages(false);
    }
  };

  useEffect(() => {
    if (open) {
      if (selectedCampaign) {
        // Create campaign data without startPublishDate
        const campaignData = { ...DEFAULT_CAMPAIGN, ...selectedCampaign };

        // Remove startPublishDate from form data if it exists
        if (campaignData.startPublishDate) {
          delete campaignData.startPublishDate;
        }

        setFormData(campaignData);
      } else {
        // Reset to defaults for new campaigns and set a random color
        setFormData({
          ...DEFAULT_CAMPAIGN,
          color: getRandomVintageColor(),
        });
      }

      // Fetch data for dropdown selectors
      fetchIdealCustomerProfiles();
      fetchContentStrategies();
      fetchBrandVoices();
      fetchBrandImages();
    }
  }, [open, selectedCampaign, companyId]);

  const handleClose = () => {
    onClose();
    setFormData(DEFAULT_CAMPAIGN);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {formData.id
          ? "Edit Content Campaign"
          : "New Content Campaign"}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}>
          <TextField
            label="Name"
            fullWidth
            value={formData.name || ""}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter campaign name..."
            required
            inputProps={{ maxLength: CAMPAIGN_FIELD_LIMITS.name }}
            helperText={`${formData.name?.length || 0}/${CAMPAIGN_FIELD_LIMITS.name} characters`}
          />

          <Box>
            <Typography
              variant="subtitle1"
              gutterBottom
              display="flex"
              alignItems="center"
            >
              <Lightbulb sx={{ mr: 1 }} fontSize="small" />
              Content Creation Settings
            </Typography>

            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}
            >
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isIdeationMode}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          isIdeationMode: e.target.checked,
                          // Reset numberOfPosts to 1 when ideation mode is turned off
                          numberOfPosts: e.target.checked ? formData.numberOfPosts : 1,
                        })
                      }
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        Enable Content Ideation Mode
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Generate multiple hooks/titles for each content item
                      </Typography>
                    </Box>
                  }
                />
              </FormGroup>

              {formData.isIdeationMode && (
                <Box>
                  <FormLabel>
                    Number of Content Items to Generate per type
                  </FormLabel>
                  <Slider
                    value={formData.numberOfPosts}
                    onChange={(_, newValue) => {
                      const newPostCount = newValue as number;
                      setFormData({
                        ...formData,
                        numberOfPosts: newPostCount,
                      });
                    }}
                    step={1}
                    marks={[
                      { value: 1, label: "1" },
                      { value: 5, label: "5" },
                      { value: 10, label: "10" },
                      { value: 20, label: "20" },
                      { value: 35, label: "35" },
                    ]}
                    min={1}
                    max={35}
                    valueLabelDisplay="auto"
                  />
                  <Typography variant="caption" color="text.secondary">
                    {formData.numberOfPosts === 1
                      ? "Generate a single content item"
                      : `Generate ${formData.numberOfPosts} content items for your calendar`}
                  </Typography>
                </Box>
              )}

              <PopOutTextInput
                label="Campaign Plan"
                multiline
                rows={4}
                fullWidth
                value={formData.plan || ""}
                onChange={(value) =>
                  setFormData({ ...formData, plan: value })
                }
                placeholder="Enter detailed campaign plan..."
                required
                dialogTitle="Campaign Plan"
                dialogDescription="Describe your campaign goals, target audience, and content themes."
                popOutRows={12}
                maxLength={CAMPAIGN_FIELD_LIMITS.plan}
                helperText={`${formData.plan?.length || 0}/${CAMPAIGN_FIELD_LIMITS.plan} characters`}
                aiContext="This is a content campaign plan. Include the campaign goals, target audience, key messages, content themes, tone of voice, and any specific requirements or constraints."
                getToken={getToken as () => Promise<string>}
                companyId={companyId}
              />

              {formData.numberOfPosts > 1 && (
                <>
                  <Box
                    sx={{
                      bgcolor: "info.lighter",
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="info.dark"
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <Info fontSize="small" sx={{ mr: 1 }} />
                      {formData.numberOfPosts > 1
                        ? `All ${formData.numberOfPosts} content items will be scheduled automatically starting from the next available date based on your publishing frequency and day preferences.`
                        : "Your content will be scheduled for the next available date based on your preferences."}
                    </Typography>
                  </Box>

                  {formData.numberOfPosts > 1 &&
                    (formData.frequency === "weekly" ||
                      formData.frequency === "monthly" ||
                      formData.frequency === "manual") && (
                      <Box sx={{ mt: 3 }}>
                        <FormControl component="fieldset" fullWidth>
                          <FormLabel component="legend">
                            Publishing Days
                          </FormLabel>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            Select which days of the week to publish content
                          </Typography>
                          <Grid container spacing={1}>
                            {[
                              "Monday",
                              "Tuesday",
                              "Wednesday",
                              "Thursday",
                              "Friday",
                              "Saturday",
                              "Sunday",
                            ].map((day, index) => (
                              <Grid item xs={6} sm={4} md={3} key={day}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={(
                                        formData.publishDaysOfWeek || []
                                      ).includes(index)}
                                      onChange={(e) => {
                                        const newDays = e.target.checked
                                          ? [
                                              ...(formData.publishDaysOfWeek ||
                                                []),
                                              index,
                                            ].sort()
                                          : (
                                              formData.publishDaysOfWeek || []
                                            ).filter((d: any) => d !== index);

                                        // Ensure at least one day is selected
                                        if (newDays.length > 0) {
                                          setFormData({
                                            ...formData,
                                            publishDaysOfWeek: newDays,
                                          });
                                        }
                                      }}
                                    />
                                  }
                                  label={day}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </FormControl>
                      </Box>
                    )}
                </>
              )}
            </Box>
          </Box>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.webResearch}
                  onChange={(e) =>
                    setFormData({ ...formData, webResearch: e.target.checked })
                  }
                />
              }
              label="Enable Web Research"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.knowledgeBase}
                  onChange={(e) => {
                    // When turning off knowledge base, reset document scope to root
                    if (!e.target.checked) {
                      setFormData({
                        ...formData,
                        knowledgeBase: false,
                        knowledgeBaseScope: "/",
                      });
                    } else {
                      setFormData({
                        ...formData,
                        knowledgeBase: true,
                      });
                    }
                  }}
                />
              }
              label="Enable Knowledge Base"
            />
            {formData.knowledgeBase && (
              <Box sx={{ mt: 2 }}>
                <KnowledgeBaseSelect
                  getToken={getToken}
                  eventListener={eventListener}
                  company={company}
                  value={formData.knowledgeBaseScope}
                  onValueChange={(v) =>
                    setFormData({ ...formData, knowledgeBaseScope: v })
                  }
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  Select a specific folder to narrow down the documents used for
                  knowledge base context
                </Typography>
              </Box>
            )}
          </FormGroup>

          <TextField
            select
            fullWidth
            value={formData.tone || ""}
            label="Tone"
            required
            onChange={(e) => setFormData({ ...formData, tone: e.target.value })}
            placeholder="Select tone..."
          >
            <MenuItem value="professional">Professional</MenuItem>
            <MenuItem value="casual">Casual</MenuItem>
            <MenuItem value="friendly">Friendly</MenuItem>
            <MenuItem value="authoritative">Authoritative</MenuItem>
          </TextField>
          <TextField
            select
            fullWidth
            value={formData.frequency || "manual"}
            label="Auto-Run & Publishing Frequency"
            required
            onChange={(e) =>
              setFormData({ ...formData, frequency: e.target.value })
            }
            helperText={
              formData.numberOfPosts > 1
                ? "For multi-post campaigns, this also determines the interval between scheduled posts"
                : "How often this campaign should run automatically"
            }
          >
            <MenuItem value="manual">Manual (Run on demand)</MenuItem>
            <MenuItem value="daily">Daily (Every day)</MenuItem>
            <MenuItem value="weekly">Weekly (Every 7 days)</MenuItem>
            <MenuItem value="monthly">Monthly (Every 30 days)</MenuItem>
          </TextField>
          <Divider sx={{ my: 2 }} />
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ display: "flex", alignItems: "center" }}
          >
            Optional Resources
            <Tooltip
              title="These optional resources can enhance your content generation by providing structured data for the AI to use"
              arrow
            >
              <Info fontSize="small" color="primary" sx={{ ml: 1 }} />
            </Tooltip>
          </Typography>

          {/* Ideal Customer Profile Selector */}
          <FormControl fullWidth>
            <InputLabel>Ideal Customer Profile (Optional)</InputLabel>
            <Select
              value={formData.idealCustomerProfileId || ""}
              label="Ideal Customer Profile (Optional)"
              onChange={(e) =>
                setFormData({
                  ...formData,
                  idealCustomerProfileId: e.target.value,
                })
              }
              disabled={fetchingICPs}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {idealCustomerProfiles.map((profile) => (
                <MenuItem key={profile.id} value={profile.id}>
                  {profile.name}
                </MenuItem>
              ))}
            </Select>
            {fetchingICPs && <LinearProgress sx={{ mt: 1 }} />}
          </FormControl>

          {/* Content Strategy Selector */}
          <FormControl fullWidth>
            <InputLabel>Content Strategy (Optional)</InputLabel>
            <Select
              value={formData.contentStrategyId || ""}
              label="Content Strategy (Optional)"
              onChange={(e) =>
                setFormData({ ...formData, contentStrategyId: e.target.value })
              }
              disabled={fetchingStrategies}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {contentStrategies.map((strategy) => (
                <MenuItem key={strategy.id} value={strategy.id}>
                  {strategy.name}
                </MenuItem>
              ))}
            </Select>
            {fetchingStrategies && <LinearProgress sx={{ mt: 1 }} />}
          </FormControl>

          {/* Brand Voice Selector */}
          <FormControl fullWidth>
            <InputLabel>Brand Voice (Optional)</InputLabel>
            <Select
              value={formData.brandVoiceId || ""}
              label="Brand Voice (Optional)"
              onChange={(e) =>
                setFormData({ ...formData, brandVoiceId: e.target.value })
              }
              disabled={fetchingVoices}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {brandVoices.map((voice) => (
                <MenuItem key={voice.id} value={voice.id}>
                  {voice.name}
                </MenuItem>
              ))}
            </Select>
            {fetchingVoices && <LinearProgress sx={{ mt: 1 }} />}
          </FormControl>

          {/* Brand Image Selector */}
          <FormControl fullWidth>
            <InputLabel>Brand Image (Optional)</InputLabel>
            <Select
              value={formData.brandImageId || ""}
              label="Brand Image (Optional)"
              onChange={(e) =>
                setFormData({ ...formData, brandImageId: e.target.value })
              }
              disabled={fetchingImages}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {brandImages.map((image) => (
                <MenuItem key={image.id} value={image.id}>
                  {image.name}
                </MenuItem>
              ))}
            </Select>
            {fetchingImages && <LinearProgress sx={{ mt: 1 }} />}
          </FormControl>

          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Campaign Color
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {vintageColors.map((color) => (
                <Chip
                  key={color}
                  label={color === formData.color ? "Selected" : " "}
                  sx={{
                    bgcolor: color,
                    color: getContrastColor(color),
                    cursor: "pointer",
                    width: "74px",
                    height: "32px",
                    border:
                      color === formData.color ? "2px solid #000" : "none",
                  }}
                  onClick={() => setFormData({ ...formData, color })}
                />
              ))}
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Content Types *
            </Typography>
            <CampaignTypeSelect
              value={formData.channels}
              onChange={(v) => setFormData({ ...formData, channels: v })}
              companyId={companyId}
              getToken={getToken}
            />
            {!hasContentTypeSelected() && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
                Please select at least one content type
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <LoadingButton
          loading={loading}
          onClick={handleSubmit}
          variant="contained"
          disabled={!validForm()}
        >
          {formData.id
            ? "Save"
            : formData.isIdeationMode
            ? "Create Content with Ideation"
            : formData.numberOfPosts > 1
            ? "Create Content Calendar"
            : "Create Campaign"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
